import './Features.css';
import TileComponent from '../../Components/TileComponent';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Topbar from '../../Components/Topbar';
import CategoryBar from '../../Components/CategoryBar';
import Header from '../../Components/Header';
import { Snackbar, Alert } from '@mui/material';
import { debounce } from 'lodash';

function Features() {
  const [groups, setGroups] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local state for immediate input display

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('http://127.0.0.1:5000/api/data');
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();
      setGroups(Object.keys(data));
      setFeatures(Object.values(data));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  const handleToggle = (feature) => {
    if (selectedItems.some((item) => item.title === feature.title)) {
      setSelectedItems(selectedItems.filter((item) => item.title !== feature.title));
    } else {
      setSelectedItems([...selectedItems, feature]);
    }
  };

  const handleRemove = (title) => {
    setSelectedItems(selectedItems.filter((item) => item.title !== title));
  };

  const handleFindBestPlan = () => {
    navigate('/recommendations', { state: { selectedFeatures: selectedItems } });
  };

  const handleAddToCart = (item) => {
    const { title, quantity } = item;
    const existingItem = selectedItems.find(item => item.title === title);

    if (existingItem) {
      setSelectedItems(
        selectedItems.map(item =>
          item.title === title
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
    } else {
      setSelectedItems([...selectedItems, { title, quantity }]);
    }

    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // Debounced function to set the search term, reducing filtering frequency
  const debouncedSetSearchTerm = useMemo(
    () => debounce((term) => setSearchTerm(term), 300),
    []
  );

  const handleSearchChange = (term) => {
    setSearchInput(term); // Update immediate input display
    debouncedSetSearchTerm(term); // Debounced update for filtering
  };

  // Memoized flat list of all features
  const allFeatures = useMemo(() => features.flat(), [features]);

  // Memoized filtered features
  const filteredFeatures = useMemo(() => {
    return searchTerm
      ? allFeatures.filter((feature) =>
          feature.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : features[currentIndex] || [];
  }, [searchTerm, allFeatures, features, currentIndex]);

  return (
    <Box>
      {/* Topbar */}
      <Topbar
        selectedItems={selectedItems}
        handleRemove={handleRemove}
        onSearch={handleSearchChange} // Pass search change handler to Topbar
      />

      {/* Main Container */}
      <Box sx={{ display: 'flex', marginTop: '60px' }}> {/* Adjusted marginTop to match Topbar height */}
        {/* Category Sidebar */}
        <CategoryBar
          groups={groups}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />

        {/* Main Content */}
        <Box sx={{ marginLeft: '220px', padding: '20px', flexGrow: 1 }}> {/* Adjusted marginLeft to accommodate sidebar width */}
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            {/* Empty Box to Balance the Flex Layout */}
            <Box sx={{ flex: 1 }}></Box>

            {/* Centered Title */}
            <Typography
              sx={{
                flex: 2,
                textAlign: 'center',
                fontSize: '1.5rem',
                color: 'rgb(17, 63, 131)',
                fontWeight: 600,
                fontFamily: "'San Francisco', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              }}
            >
              Choose Your Service Plan
            </Typography>

            {/* Find Best Plan Button */}
            <Box sx={{ flex: 1, textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFindBestPlan}
              >
                Find Best Plan
              </Button>
            </Box>
          </Box>

          {/* Feature Tiles */}
          {!loading && (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} // Single column on extra-small screens, two columns on small and above
              gap={3}
              justifyContent="center"
            >
              {filteredFeatures.length === 0 ? (
                <Typography>No service plans match your search criteria.</Typography>
              ) : (
                filteredFeatures.map((feature, idx) => (
                  <TileComponent
                    key={idx}
                    title={feature.title}
                    description={feature.Description}
                    url={feature.URL}
                    onAddToCart={handleAddToCart}
                    isChecked={selectedItems.some(
                      (item) => item.title === feature.title
                    )}
                    onToggle={() => handleToggle(feature)}
                  />
                ))
              )}
            </Box>
          )}

          {/* Snackbar Notification */}
          <Snackbar
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Positioning the snackbar at the bottom center
          >
            <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
              Service added to cart!
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}

export default Features;